// Payments (roadmap Phase 1.4) — the screens: Record payment, the Payments tab of the Invoices
// window, a payment's detail (what it paid, Apply credit, Void), and the printable receipt.
// Writes go through functions/payments.js (recordPayment / applyCredit / voidPayment), which
// update the invoices in transactions; this file never changes an amount itself.

import { database, functions } from './initFirebase';
import { ref, get, query, orderByChild, equalTo } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { idbCursorAll, toastMessage, escapeHTML } from './utilities';
import { toCents, toDollars, formatMoney } from './money';
import { openCompose, pagesToPdf, businessProfile } from './mailClient';
import { openEmailHistory } from './emailHistory';

const esc = v => escapeHTML(v == null ? '' : String(v));
const call = (name, data) => httpsCallable(functions, name, { timeout: 60000 })(data).then(r => r.data);
const errText = e => String(e?.message || e).replace(/^FirebaseError:\s*/, '');
const changed = () => window.dispatchEvent(new CustomEvent('invoices:changed'));

export const METHODS = { check: 'Check', cash: 'Cash', card: 'Card', ach: 'Bank transfer', other: 'Other' };

let deps = { openInvoice: () => {} };
/** invoices.js passes how to open an invoice (avoids an import loop). */
export function setPaymentsDeps(d) { deps = { ...deps, ...d }; }

const fmtDate = ymd => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const customerName = c => [c?.First_Name, c?.Last_Name].filter(Boolean).join(' ') || c?.Company || 'Customer';

// One customer's invoices only (indexed by customerId), never all of /invoices.
const invoicesOf = async customerId => (await get(query(ref(database, 'invoices'), orderByChild('customerId'), equalTo(customerId)))).val() || {};

async function openInvoicesFor(customerId) {
  const snap = await invoicesOf(customerId);
  return Object.values(snap)
    .filter(i => i && i.customerId === customerId && (i.status === 'sent' || i.status === 'partial') && i.balanceCents > 0)
    .sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)) || (a.issuedAt || 0) - (b.issuedAt || 0));
}

/* ============ Record payment ============ */

/**
 * The Record payment window. opts: { customerId?, invoiceId? } — from an invoice it starts with that
 * invoice's balance applied to it; from the Payments tab it starts with a customer search.
 */
export async function openPaymentDialog(opts = {}) {
  document.getElementById('pay-dialog')?.remove();
  const P = { customerId: opts.customerId || null, customer: null, invoices: [], first: opts.invoiceId || null, saving: false };
  const wrap = document.createElement('div');
  wrap.id = 'pay-dialog';
  wrap.className = 'inv-root inv-backdrop pay-backdrop';
  document.body.appendChild(wrap);

  const close = () => wrap.remove();
  const load = async () => {
    P.customer = P.customerId ? (await get(ref(database, `customers/${P.customerId}`))).val() : null;
    P.invoices = P.customerId ? await openInvoicesFor(P.customerId) : [];
    if (P.first) P.invoices.sort((a, b) => (a.id === P.first ? -1 : b.id === P.first ? 1 : 0));
    render();
    const total = P.first ? (P.invoices.find(i => i.id === P.first)?.balanceCents || 0) : 0;
    if (total) { wrap.querySelector('[data-k="amount"]').value = toDollars(total).toFixed(2); spread(); }
  };

  // Fill "apply" boxes oldest first from the amount received; the rest is credit.
  const spread = () => {
    let left = toCents(wrap.querySelector('[data-k="amount"]')?.value);
    wrap.querySelectorAll('[data-apply]').forEach(input => {
      const inv = P.invoices.find(i => i.id === input.dataset.apply);
      const take = Math.max(0, Math.min(left, inv.balanceCents));
      input.value = take ? toDollars(take).toFixed(2) : '';
      left -= take;
    });
    summary();
  };
  const summary = () => {
    const amount = toCents(wrap.querySelector('[data-k="amount"]')?.value);
    let applied = 0;
    wrap.querySelectorAll('[data-apply]').forEach(i => { applied += toCents(i.value); });
    const el = wrap.querySelector('[data-k="summary"]');
    if (!el) return;
    const credit = amount - applied;
    el.innerHTML = applied > amount
      ? `<span class="inv-warn">${formatMoney(applied)} applied is more than the ${formatMoney(amount)} received.</span>`
      : `${formatMoney(applied)} to invoices${credit ? ` · <b>${formatMoney(credit)}</b> kept as credit for ${esc(customerName(P.customer))}` : ''}`;
  };

  function render() {
    wrap.innerHTML = `
      <div class="inv-panel pay-panel" role="dialog" aria-modal="true" aria-labelledby="pay-title">
        <div class="inv-head">
          <div class="inv-title" id="pay-title">Record payment</div>
          <button type="button" class="inv-x" data-act="close" aria-label="Close">&times;</button>
        </div>
        <div class="inv-body inv-ed-body">
          ${P.customer ? `<div class="inv-field"><span>From</span><div class="inv-who-name">${esc(customerName(P.customer))}${opts.customerId ? '' : ' <button type="button" class="inv-link" data-act="change-customer">Change</button>'}</div></div>` : `
            <div class="inv-ed-who"><label class="inv-field inv-grow"><span>From (customer)</span>
              <input type="search" data-k="cust-search" placeholder="Type a name, boat or email" autocomplete="off"></label>
              <div class="inv-cust-results" id="pay-cust-results"></div></div>`}
          <div class="pay-grid">
            <label class="inv-field"><span>Amount received</span><input data-k="amount" inputmode="decimal" placeholder="0.00"></label>
            <label class="inv-field"><span>Method</span><select data-k="method">${Object.entries(METHODS).map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}</select></label>
            <label class="inv-field"><span>Reference</span><input data-k="reference" maxlength="80" placeholder="Check #, last 4 of card…"></label>
            <label class="inv-field"><span>Date received</span><input type="date" data-k="date" value="${todayYmd()}" max="${todayYmd()}"></label>
          </div>
          ${P.customer ? `
            <div class="inv-field"><span>Apply to</span>
              ${P.invoices.length ? `<table class="inv-lines pay-apply"><tbody>
                ${P.invoices.map(i => `<tr><td><b>${esc(i.number)}</b> <span class="inv-muted">${i.dueDate ? `due ${esc(fmtDate(i.dueDate))}` : ''}${i.boatName ? ` · ${esc(i.boatName)}` : ''}</span></td>
                  <td class="inv-num inv-muted">owes ${formatMoney(i.balanceCents)}</td>
                  <td class="inv-num"><input class="inv-in inv-in-num" data-apply="${esc(i.id)}" inputmode="decimal" placeholder="0.00"></td></tr>`).join('')}
              </tbody></table>` : '<div class="inv-muted">No open invoices. The whole amount is kept as credit.</div>'}
            </div>
            <div class="inv-muted" data-k="summary"></div>` : ''}
          <label class="inv-field"><span>Note (internal)</span><input data-k="note" maxlength="500"></label>
        </div>
        <div class="inv-foot">
          <span class="inv-spacer"></span>
          <button type="button" class="inv-btn" data-act="close">Cancel</button>
          <button type="button" class="inv-btn inv-primary" data-act="save"${P.customer ? '' : ' disabled'}>Record payment</button>
        </div>
      </div>`;
  }

  let custCache = null;
  wrap.addEventListener('input', async e => {
    const k = e.target.dataset.k;
    if (k === 'amount') spread();
    else if (e.target.dataset.apply) summary();
    else if (k === 'cust-search') {
      const box = document.getElementById('pay-cust-results');
      const q = e.target.value.trim().toLowerCase();
      if (q.length < 2) { box.innerHTML = ''; return; }
      custCache ||= (await idbCursorAll('customerData')).filter(c => c && c.id && c.id !== 'lastUpdated');
      box.innerHTML = custCache.filter(c => [customerName(c), c.Email, ...Object.values(c.Boats || {}).map(b => b?.Boat_Name)]
        .some(v => String(v || '').toLowerCase().includes(q))).slice(0, 8)
        .map(c => `<button type="button" class="inv-cust-hit" data-cust="${esc(c.id)}"><b>${esc(customerName(c))}</b></button>`).join('')
        || '<div class="inv-muted">No customer matches.</div>';
    }
  });
  wrap.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  wrap.addEventListener('click', async e => {
    if (e.target === wrap) return close();
    const hit = e.target.closest('[data-cust]');
    if (hit) { P.customerId = hit.dataset.cust; return load(); }
    const act = e.target.closest('[data-act]')?.dataset.act;
    if (act === 'close') return close();
    if (act === 'change-customer') { P.customerId = null; P.customer = null; P.invoices = []; return render(); }
    if (act !== 'save' || P.saving) return;
    const amountCents = toCents(wrap.querySelector('[data-k="amount"]').value);
    if (amountCents <= 0) { toastMessage('Enter the amount received', 'red'); return; }
    const applications = {};
    wrap.querySelectorAll('[data-apply]').forEach(i => { const c = toCents(i.value); if (c > 0) applications[i.dataset.apply] = c; });
    const applied = Object.values(applications).reduce((a, b) => a + b, 0);
    if (applied > amountCents) { toastMessage('More is applied than was received', 'red'); return; }
    P.saving = true;
    const btn = e.target.closest('[data-act]');
    btn.disabled = true;
    btn.textContent = 'Recording…';
    try {
      const pay = await call('recordPayment', {
        customerId: P.customerId, amountCents, applications,
        method: wrap.querySelector('[data-k="method"]').value,
        reference: wrap.querySelector('[data-k="reference"]').value,
        receivedDate: wrap.querySelector('[data-k="date"]').value,
        note: wrap.querySelector('[data-k="note"]').value,
      });
      changed();
      close();
      toastMessage(`${pay.number}: ${formatMoney(pay.amountCents)} recorded`, 'green', 4000);
      openPaymentDetail(pay);
    } catch (err) {
      toastMessage(errText(err), 'red', 7000);
      P.saving = false;
      btn.disabled = false;
      btn.textContent = 'Record payment';
    }
  });

  if (P.customerId) await load(); else render();
}

/* ============ Payments tab ============ */

/** Rows for the Invoices window's Payments tab. */
export async function renderPaymentsList(box, search) {
  const all = Object.values((await get(ref(database, 'payments'))).val() || {}).filter(p => p && p.id);
  const q = String(search || '').trim().toLowerCase();
  const rows = all
    .filter(p => !q || [p.number, p.customerName, p.reference].some(v => String(v || '').toLowerCase().includes(q)))
    .sort((a, b) => String(b.receivedDate).localeCompare(String(a.receivedDate)) || (b.createdAt || 0) - (a.createdAt || 0));
  box.innerHTML = rows.length ? rows.map(p => `
    <button type="button" class="inv-row" data-payment="${esc(p.id)}">
      <span class="inv-row-num">${esc(p.number)}</span>
      <span class="inv-row-main">
        <span class="inv-row-name">${esc(p.customerName)}</span>
        <span class="inv-row-sub">${esc(fmtDate(p.receivedDate))} · ${esc(METHODS[p.method] || p.method)}${p.reference ? ` ${esc(p.reference)}` : ''}${p.status === 'ok' && p.unappliedCents ? ` · <b>${formatMoney(p.unappliedCents)} credit left</b>` : ''}</span>
      </span>
      ${p.status === 'void' ? '<span class="inv-pill inv-pill-void">Void</span>' : ''}
      <span class="inv-row-total">${formatMoney(p.amountCents)}</span>
    </button>`).join('') : '<div class="inv-empty">No payments recorded yet.</div>';
}

/* ============ payment detail ============ */

export async function openPaymentDetail(payOrId) {
  const pay = typeof payOrId === 'string' ? (await get(ref(database, `payments/${payOrId}`))).val() : payOrId;
  if (!pay) return;
  document.getElementById('pay-detail')?.remove();
  const invs = await invoicesOf(pay.customerId);
  const open = Object.values(invs).filter(i => i && i.customerId === pay.customerId && (i.status === 'sent' || i.status === 'partial') && i.balanceCents > 0);
  const wrap = document.createElement('div');
  wrap.id = 'pay-detail';
  wrap.className = 'inv-root inv-backdrop pay-backdrop';
  const apps = Object.entries(pay.applications || {}).filter(([, c]) => c);
  wrap.innerHTML = `
    <div class="inv-panel pay-panel" role="dialog" aria-modal="true" aria-labelledby="pay-d-title">
      <div class="inv-head">
        <div class="inv-title" id="pay-d-title">${esc(pay.number)}</div>
        ${pay.status === 'void' ? '<span class="inv-pill inv-pill-void">Void</span>' : '<span class="inv-pill inv-pill-paid">Received</span>'}
        <button type="button" class="inv-x" data-act="close" aria-label="Close">&times;</button>
      </div>
      <div class="inv-body inv-ed-body">
        <div class="pay-facts">
          <div><span>From</span><b>${esc(pay.customerName)}</b></div>
          <div><span>Amount</span><b>${formatMoney(pay.amountCents)}</b></div>
          <div><span>Method</span><b>${esc(METHODS[pay.method] || pay.method)}${pay.reference ? ` · ${esc(pay.reference)}` : ''}</b></div>
          <div><span>Received</span><b>${esc(fmtDate(pay.receivedDate))}</b></div>
        </div>
        <div class="inv-field"><span>Applied to</span>
          ${apps.length ? apps.map(([id, c]) => `<div class="pay-app-row"><button type="button" class="inv-link" data-open-inv="${esc(id)}">${esc(invs[id]?.number || 'Invoice')}</button><span>${formatMoney(c)}</span></div>`).join('') : '<div class="inv-muted">Nothing yet.</div>'}
          ${pay.status === 'ok' && pay.unappliedCents ? `<div class="pay-app-row"><span><b>Credit left</b></span><b>${formatMoney(pay.unappliedCents)}</b></div>` : ''}
        </div>
        ${pay.status === 'ok' && pay.unappliedCents && open.length ? `
          <div class="pay-grid pay-credit">
            <label class="inv-field inv-grow"><span>Use credit on</span><select data-k="credit-inv">${open.map(i => `<option value="${esc(i.id)}">${esc(i.number)} · owes ${formatMoney(i.balanceCents)}</option>`).join('')}</select></label>
            <label class="inv-field"><span>Amount</span><input data-k="credit-amt" inputmode="decimal" value="${toDollars(Math.min(pay.unappliedCents, open[0].balanceCents)).toFixed(2)}"></label>
            <button type="button" class="inv-btn" data-act="apply-credit">Apply credit</button>
          </div>` : ''}
        ${pay.note ? `<div class="inv-muted">Note: ${esc(pay.note)}</div>` : ''}
        ${pay.status === 'void' ? `<div class="inv-muted">Voided ${esc(new Date(pay.voidedAt).toLocaleDateString())} by ${esc(pay.voidedBy || '')}: ${esc(pay.voidReason || '')}</div>` : ''}
        <div class="inv-muted">Recorded by ${esc(pay.createdBy || '')} on ${esc(new Date(pay.createdAt).toLocaleString())}</div>
      </div>
      <div class="inv-foot">
        ${pay.status === 'ok' ? '<button type="button" class="inv-btn inv-danger-ghost" data-act="void">Void…</button>' : ''}
        <span class="inv-spacer"></span>
        <button type="button" class="inv-btn" data-act="receipt">Receipt / PDF</button>
        ${pay.status === 'ok' ? '<button type="button" class="inv-btn inv-primary" data-act="email">Email receipt</button>' : ''}
        <button type="button" class="inv-btn" data-act="close">Close</button>
      </div>
    </div>`;
  document.body.appendChild(wrap);
  const close = () => wrap.remove();
  wrap.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  wrap.addEventListener('click', async e => {
    if (e.target === wrap) return close();
    const inv = e.target.closest('[data-open-inv]')?.dataset.openInv;
    if (inv) { close(); return deps.openInvoice(inv); }
    const act = e.target.closest('[data-act]')?.dataset.act;
    if (act === 'close') return close();
    if (act === 'receipt' || act === 'email') return showReceipt(pay, invs, { email: act === 'email', back: () => openPaymentDetail(pay.id) });
    if (act === 'apply-credit') {
      const invoiceId = wrap.querySelector('[data-k="credit-inv"]').value;
      const amountCents = toCents(wrap.querySelector('[data-k="credit-amt"]').value);
      try {
        const r = await call('applyCredit', { paymentId: pay.id, invoiceId, amountCents });
        changed();
        toastMessage(`${formatMoney(amountCents)} credit applied to ${r.invoice.number}`, 'green');
        openPaymentDetail(pay.id);
      } catch (err) { toastMessage(errText(err), 'red', 7000); }
    }
    if (act === 'void') {
      const reason = prompt(`Void ${pay.number}? It's taken back off every invoice it paid.\n\nReason:`);
      if (!reason || !reason.trim()) return;
      try {
        await call('voidPayment', { id: pay.id, reason: reason.trim() });
        changed();
        toastMessage(`${pay.number} voided`, 'green');
        openPaymentDetail(pay.id);
      } catch (err) { toastMessage(errText(err), 'red', 7000); }
    }
  });
}

/* ============ receipt ============ */

/** The printable receipt (same page geometry and print rules as estimates and invoices). */
async function showReceipt(pay, invs, { email = false, back } = {}) {
  document.getElementById('pay-detail')?.remove();
  const biz = await businessProfile();
  document.getElementById('receiptContainer1')?.remove();
  const box = document.createElement('div');
  box.id = 'receiptContainer1';
  box.className = 'estimateContainer';
  const apps = Object.entries(pay.applications || {}).filter(([, c]) => c);
  box.innerHTML = `
    <div class="estimatePDF">
      <div class="no-print estDoc-toolbar">
        <button type="button" data-r="print" class="rcpt-btn-primary">Print / Download PDF</button>
        ${pay.status === 'ok' ? '<button type="button" data-r="email" class="estDoc-email-btn">Email to customer</button>' : ''}
        ${pay.id ? '<button type="button" data-r="emails">Emails</button>' : ''}
        <button type="button" data-r="close" class="rcpt-btn">Close</button>
      </div>
      <div class="invDoc-page" id="rcptPage1">
        <div class="estDoc-letterhead">
          <img src="/images/logo/logo-512.webp" class="estDoc-logo" alt="">
          <div class="estDoc-title-block">
            <div class="invDoc-title-row">${pay.status === 'void' ? '<span class="invDoc-past-due-pill invDoc-pill-void">VOID</span>' : ''}<div class="estDoc-title">RECEIPT</div></div>
            <div class="estDoc-number">${esc(pay.number)}</div>
          </div>
        </div>
        <div class="estDoc-meta-row">
          <div class="estDoc-meta-col estDoc-meta-col-wide">
            <div class="estDoc-meta-label">Received from</div>
            <div class="estDoc-meta-name">${esc(pay.customerName)}</div>
            <div class="estDoc-meta-sub">${esc(pay.customerEmail || '')}</div>
          </div>
          <div class="estDoc-meta-dates">
            <div class="estDoc-meta-dates-labels"><div>Date received</div><div>Method</div><div>Reference</div></div>
            <div class="estDoc-meta-dates-values"><div>${esc(fmtDate(pay.receivedDate))}</div><div>${esc(METHODS[pay.method] || pay.method)}</div><div>${esc(pay.reference || '—')}</div></div>
          </div>
        </div>
        <table class="invDoc-line-table">
          <thead><tr><th>Applied to</th><th class="estDoc-num">Amount</th></tr></thead>
          <tbody>
            ${apps.map(([id, c]) => `<tr><td><div class="invDoc-line-name">Invoice ${esc(invs[id]?.number || '')}</div>${invs[id]?.boatName ? `<div class="invDoc-line-sub">${esc(invs[id].boatName)}</div>` : ''}</td><td class="estDoc-num">${formatMoney(c)}</td></tr>`).join('')}
            ${pay.unappliedCents ? `<tr><td><div class="invDoc-line-name">Credit on account</div><div class="invDoc-line-sub">Applied to a future invoice</div></td><td class="estDoc-num">${formatMoney(pay.unappliedCents)}</td></tr>` : ''}
          </tbody>
        </table>
        <div class="estDoc-totals-stack invDoc-totals-stack">
          <div class="invDoc-balance-due-row"><span>AMOUNT RECEIVED</span><span>${formatMoney(pay.amountCents)}</span></div>
        </div>
        <div class="estDoc-terms-block invDoc-terms-block">
          <div class="estDoc-terms-text">Thank you for your payment.${pay.status === 'void' ? ' This receipt was voided.' : ''}</div>
        </div>
        <div class="estDoc-footer">
          <span>${esc([biz.name, biz.address, biz.phone, biz.supportEmail].filter(Boolean).join(' · '))}</span>
          <span class="estDoc-page-num">Page 1 of 1</span>
        </div>
      </div>
    </div>`;
  document.body.appendChild(box);
  const list = document.getElementById('inv-list-overlay');
  if (list) list.style.display = 'none';
  const close = () => { box.remove(); if (list?.isConnected) list.style.display = ''; back?.(); };
  const send = async () => {
    const label = `Receipt ${pay.number}`;
    const pdf = pagesToPdf([box.querySelector('#rcptPage1')], `${label}.pdf`);
    openCompose({
      to: pay.customerEmail || '',
      templateId: 'receipt',
      vals: { receipt_no: pay.number, amount: formatMoney(pay.amountCents), paid_date: fmtDate(pay.receivedDate) },
      attachments: pdf, customerId: pay.customerId, docType: 'receipt', docLabel: label,
      recordType: 'payment', recordId: pay.id || null,
    });
  };
  box.addEventListener('click', e => {
    const r = e.target.closest('[data-r]')?.dataset.r;
    if (r === 'close') close();
    if (r === 'print') window.print();
    if (r === 'email') send();
    if (r === 'emails') openEmailHistory({ recordId: pay.id }, { title: `Receipt ${pay.number}`, sub: [pay.customerName, formatMoney(pay.amountCents)].filter(Boolean).join(' · ') });
  });
  if (email) setTimeout(send, 50);
}
